// Modules
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { AccordionModule } from 'primeng/accordion';

// Material Modules
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';

// PrimeNG Modules
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ListboxModule } from 'primeng/listbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { ButtonModule } from 'primeng/button';

// Components
import { PrimeLoaderComponent } from '@shared-module/components/prime-loader/prime-loader.component';
import { FileUploadComponent } from '@shared-module/components/file-upload/file-upload.component';
import { MainTopBarComponent } from '@shared-module/components/main-top-bar/main-top-bar.component';
import { CustomDropdownComponent } from '@shared-module/components/custom-dropdown/custom-dropdown.component';
import { ClosableCustomDropdownComponent } from '@shared-module/components/closable-custom-dropdown/closable-custom-dropdown.component';
import { CustomSuggestedComponent } from '@shared-module/components/custom-suggested/custom-suggested.component';
import { TablePaginatorComponent } from '@shared-module/components/table-paginator/table-paginator.component';
import { TableSortHeaderComponent } from '@shared-module/components/table-sort-header/table-sort-header.component';
import { DefaultConfirmationDialogComponent } from '@shared-module/components/default-confirmation-dialog/default-confirmation-dialog.component';
import { AccidentTabsContainerComponent } from '@app/modules/accidents/shared/components/accident-tabs-container/accident-tabs-container.component';
import { AccidentDataTabComponent } from '@accidents/shared/components/accident-tabs-container/accident-data-tab/accident-data-tab.component';
import { PolicyDataTabComponent } from '@accidents/shared/components/accident-tabs-container/policy-data-tab/policy-data-tab.component';
import { ClaimDataTabComponent } from '@accidents/shared/components/accident-tabs-container/claim-data-tab/claim-data-tab.component';
import { ImagesDataTabComponent } from '@accidents/shared/components/accident-tabs-container/images-data-tab/images-data-tab.component';
import { ImagesDataModalComponent } from '@accidents/shared/components/accident-tabs-container/images-data-tab/images-data-modal/images-data-modal.component';
import { ClaimantsDataTabComponent } from '@accidents/shared/components/accident-tabs-container/claimants-data-tab/claimants-data-tab.component';
import { DefaultDialogComponent } from '@shared-module/components/default-dialog/default-dialog.component';
import { TableComponent } from '@shared-module/components/table/table.component';
import { ButtonTableConfigComponent } from '@shared-module/components/button-table-config/button-table-config.component';
import { GenericTableComponent } from '@shared-module/components/generic-table/generic-table.component';
import { ErrorBadgeComponent } from '@shared-module/components/error-badge/error-badge.component';
import { ImageZoomModalComponent } from '@shared-module/components/image-zoom-modal/image-zoom-modal.component';
import { DynamicCheckListComponent } from '@shared-module/components/dynamic-check-list/dynamic-check-list.component';
import { CustomBadgeComponent } from '@shared-module/components/custom-badge/custom-badge.component';
import { CustomModalComponent } from './components/custom-modal/custom-modal.component';
import { DefaultEliminationDialogComponent } from '@shared-module/components/default-elimination-dialog/default-elimination-dialog.component';

// Directives
import { OnlyNumberDirective } from '@shared-module/directives/only-number.directive';
import { NgxDecimalNumberDirective } from '@shared-module/directives/decimal-number.directive';
import { FileDragDropDirective } from '@shared-module/directives/file-drag-drop.directive';
import { NgxDecimalNumberEsDirective } from '@shared-module/directives/decimal-number-es.directive';
import { NgxWholeNumberDirective } from '@shared-module/directives/whole-number.directive';
import { ModalClickedDirective } from '@shared-module/directives/modal-clicked.directive';

// Services
import { NavigationService } from '@app/modules/shared/services/navigation/navigation.service';
import { AuthInterceptorService } from '@services/auth-interceptor/auth-interceptor.service';

// Pipes
import { CustomDecimalPipe } from './pipes/custom-decimal.pipe';
import { CustomDecimalTruncatePipe } from './pipes/custom-decimal-truncate.pipe';
import { CountryNamePipe } from './pipes/country-name.pipe';
import { ThousandsPointsDirective } from './directives/thousands-points.directive';
import { ClassicFormComponent } from '@shared-module/components/classic-form/classic-form.component';

import { DynamicFormImplementationComponent } from '@shared-module/components/dynamic-form-smart/dynamic-form-implementation/dynamic-form-implementation.component';
import { DynamicFormSmartExampleComponent } from '@app/modules/shared/components/dynamic-form-smart/dynamic-form-smart-example.component';
import {
  CheckboxFieldModule,
  DateAtomModule,
  DateFieldModule,
  DynamicFormModule,
  FileUploadFieldModule,
  GoogleMapFieldModule,
  NumberFieldModule,
  SelectFieldModule,
  TextAreaFieldModule,
  TextFieldModule,
} from '@switch_software/ui-components';

// Google Maps
import { MessageModule } from 'primeng/message';
import { GoogleMapFormComponent } from '@app/modules/shared/components/google-map-form/google-map-form.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { DispatcherCompaniesModalComponent } from './components/dispatcher-companies-modal/dispatcher-companies-modal.component';
import { NgxDecimalNumberDirectiveTwo } from '@shared-module/directives/decimal-number-two.directive';
import { CapitalLettertPipe } from './pipes/capital-letter.pipe';
import { TrimOnBlurDirective } from './directives/trim-on-blur.directive';
import { CopyFieldComponent } from './components/copy-field/copy-field.component';

// Set Shared Material Modules
const MATERIAL_MODULES = [
  MatTableModule,
  MatDialogModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatAutocompleteModule,
];

@NgModule({
  declarations: [
    PrimeLoaderComponent,
    FileUploadComponent,
    MainTopBarComponent,
    OnlyNumberDirective,
    ModalClickedDirective,
    NgxDecimalNumberDirective,
    NgxDecimalNumberDirectiveTwo,
    FileDragDropDirective,
    CustomDropdownComponent,
    ClosableCustomDropdownComponent,
    CustomSuggestedComponent,
    TablePaginatorComponent,
    TableSortHeaderComponent,
    DefaultConfirmationDialogComponent,
    DefaultDialogComponent,
    AccidentTabsContainerComponent,
    AccidentDataTabComponent,
    PolicyDataTabComponent,
    ClaimDataTabComponent,
    ImagesDataTabComponent,
    ImagesDataModalComponent,
    ClaimantsDataTabComponent,
    TableComponent,
    GenericTableComponent,
    ButtonTableConfigComponent,
    ErrorBadgeComponent,
    NgxDecimalNumberEsDirective,
    NgxWholeNumberDirective,
    ImageZoomModalComponent,
    CustomDecimalPipe,
    CapitalLettertPipe,
    CustomDecimalTruncatePipe,
    ThousandsPointsDirective,
    DynamicCheckListComponent,
    CustomBadgeComponent,
    CustomModalComponent,
    DefaultEliminationDialogComponent,
    ClassicFormComponent,
    DynamicFormImplementationComponent,
    DynamicFormSmartExampleComponent,
    GoogleMapFormComponent,
    DispatcherCompaniesModalComponent,
  ],
  imports: [
    CountryNamePipe,
    FormsModule,
    CommonModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ReactiveFormsModule,
    ...MATERIAL_MODULES,
    TableModule,
    DatePickerModule,
    SelectModule,
    ListboxModule,
    MultiSelectModule,
    PaginatorModule,
    CheckboxModule,
    NgMultiSelectDropDownModule.forRoot(),
    ProgressSpinnerModule,
    DynamicFormModule,
    TextFieldModule,
    NumberFieldModule,
    CheckboxFieldModule,
    SelectFieldModule,
    TextAreaFieldModule,
    FileUploadFieldModule,
    DateFieldModule,
    MessageModule,
    GoogleMapFieldModule,
    GoogleMapsModule,
    AccordionModule,
    TabsModule,
    RadioButtonModule,
    TooltipModule,
    DateAtomModule,
    TieredMenuModule,
    ButtonModule,
    TrimOnBlurDirective,
    CopyFieldComponent,
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ModalClickedDirective,
    NgxDecimalNumberDirective,
    NgxDecimalNumberDirectiveTwo,
    PrimeLoaderComponent,
    FileUploadComponent,
    MainTopBarComponent,
    OnlyNumberDirective,
    FileDragDropDirective,
    CustomDropdownComponent,
    ClosableCustomDropdownComponent,
    CustomSuggestedComponent,
    TablePaginatorComponent,
    TableSortHeaderComponent,
    DefaultConfirmationDialogComponent,
    DefaultDialogComponent,
    AccidentTabsContainerComponent,
    AccidentDataTabComponent,
    PolicyDataTabComponent,
    ClaimDataTabComponent,
    ImagesDataTabComponent,
    ImagesDataModalComponent,
    ClaimantsDataTabComponent,
    TableComponent,
    ButtonTableConfigComponent,
    GenericTableComponent,
    ErrorBadgeComponent,
    NgxDecimalNumberEsDirective,
    NgxWholeNumberDirective,
    ImageZoomModalComponent,
    ...MATERIAL_MODULES,
    CustomDecimalPipe,
    CustomDecimalTruncatePipe,
    CapitalLettertPipe,
    CountryNamePipe,
    ThousandsPointsDirective,
    DynamicCheckListComponent,
    DatePickerModule,
    SelectModule,
    CustomBadgeComponent,
    CustomModalComponent,
    ProgressSpinnerModule,
    DefaultEliminationDialogComponent,
    ClassicFormComponent,
    DefaultConfirmationDialogComponent,
    DynamicFormSmartExampleComponent,
    DynamicFormModule,
    TextFieldModule,
    NumberFieldModule,
    CheckboxFieldModule,
    SelectFieldModule,
    TextAreaFieldModule,
    FileUploadFieldModule,
    DateFieldModule,
    GoogleMapFormComponent,
    GoogleMapFieldModule,
    GoogleMapsModule,
    AccordionModule,
    TabsModule,
    CheckboxModule,
    RadioButtonModule,
    TooltipModule,
    DispatcherCompaniesModalComponent,
    DateAtomModule,
    TieredMenuModule,
    ButtonModule,
    TrimOnBlurDirective,
    CopyFieldComponent,
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        DatePipe,
        DecimalPipe,
        NavigationService,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthInterceptorService,
          multi: true,
        },
      ],
    };
  }
}
